"use client";

import { c as _c } from "react/compiler-runtime";
import React, { type JSX, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSetWaveData } from "@/contexts/TitleContext";
import { useContentTab } from "../ContentTabContext";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import MyStreamWaveChat from "./MyStreamWaveChat";
import { useWaveData } from "@/hooks/useWaveData";
import MyStreamWaveLeaderboard from "./MyStreamWaveLeaderboard";
import MyStreamWaveOutcome from "./MyStreamWaveOutcome";
import { useSearchParams, usePathname, useRouter } from "next/navigation";
import { WaveWinners } from "@/components/waves/winners/WaveWinners";
import { MyStreamWaveTab } from "@/types/waves.types";
import { MyStreamWaveTabs } from "./tabs/MyStreamWaveTabs";
import MyStreamWaveMyVotes from "./votes/MyStreamWaveMyVotes";
import MyStreamWaveFAQ from "./MyStreamWaveFAQ";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { createBreakpoint } from "react-use";
import { getHomeRoute, getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { useWaveViewMode } from "@/hooks/useWaveViewMode";
import { useWave } from "@/hooks/useWave";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import useDeviceInfo from "@/hooks/useDeviceInfo";
interface MyStreamWaveProps {
  readonly waveId: string;
}
const getContentTabPanelId = (tab: MyStreamWaveTab): string => `my-stream-wave-tabpanel-${tab.toLowerCase()}`;
const useBreakpoint = createBreakpoint({
  LG: 1024,
  S: 0
});
const MyStreamWave: React.FC<MyStreamWaveProps> = t0 => {
  const $ = _c(62);
  const {
    waveId
  } = t0;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const {
    isApp
  } = useDeviceInfo();
  const queryClient = useQueryClient();
  const {
    waves,
    directMessages
  } = useMyStream();
  let t1;
  if ($[0] !== isApp || $[1] !== pathname || $[2] !== router || $[3] !== searchParams) {
    t1 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      const basePath = getWaveHomeRoute({
        isDirectMessage: pathname.startsWith("/messages"),
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath || getHomeRoute();
      router.push(newUrl, {
        scroll: false
      });
    };
    $[0] = isApp;
    $[1] = pathname;
    $[2] = router;
    $[3] = searchParams;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1 || $[6] !== waveId) {
    t2 = {
      waveId,
      onWaveNotFound: t1
    };
    $[5] = t1;
    $[6] = waveId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    data: wave
  } = useWaveData(t2);
  let t3;
  if ($[8] !== directMessages.list || $[9] !== waveId || $[10] !== waves.list) {
    t3 = waves.list.find(w_0 => w_0.id === waveId) ?? directMessages.list.find(w => w.id === waveId);
    $[8] = directMessages.list;
    $[9] = waveId;
    $[10] = waves.list;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const waveFromList = t3;
  const t4 = waveFromList?.newDropsCount.count ?? 0;
  const t5 = waveFromList?.firstUnreadDropSerialNo ?? null;
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = {
      newDropsCount: t4,
      firstUnreadSerialNo: t5
    };
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const enhancedData = t6;
  const newDropsCount = enhancedData.newDropsCount;
  let t7;
  if ($[15] !== newDropsCount || $[16] !== wave) {
    t7 = wave ? {
      name: wave.name,
      newItemsCount: newDropsCount
    } : null;
    $[15] = newDropsCount;
    $[16] = wave;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  useSetWaveData(t7);
  const stableWaveKey = `wave-${waveId}`;
  const {
    activeContentTab
  } = useContentTab();
  const {
    viewMode,
    toggleViewMode
  } = useWaveViewMode(waveId);
  const {
    isRankWave,
    isMemesWave,
    isDm
  } = useWave(wave);
  const showGalleryToggle = !isRankWave && !isMemesWave && !isDm;
  useBreakpoint();
  let t8;
  if ($[18] !== pathname || $[19] !== queryClient || $[20] !== router || $[21] !== searchParams) {
    t8 = drop => {
      queryClient.setQueryData([QueryKey.DROP, {
        drop_id: drop.id
      }], drop as ApiDrop);
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.set("drop", drop.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[18] = pathname;
    $[19] = queryClient;
    $[20] = router;
    $[21] = searchParams;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const onDropClick = t8;
  if (!wave) {
    return null;
  }
  const t9 = showGalleryToggle ? viewMode : "chat";
  let t10;
  if ($[23] !== enhancedData.firstUnreadSerialNo || $[24] !== onDropClick || $[25] !== t9 || $[26] !== wave) {
    t10 = <MyStreamWaveChat wave={wave} firstUnreadSerialNo={enhancedData.firstUnreadSerialNo} viewMode={t9} onDropClick={onDropClick} />;
    $[23] = enhancedData.firstUnreadSerialNo;
    $[24] = onDropClick;
    $[25] = t9;
    $[26] = wave;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== onDropClick || $[29] !== wave) {
    t11 = <MyStreamWaveLeaderboard key={wave.id} wave={wave} onDropClick={onDropClick} />;
    $[28] = onDropClick;
    $[29] = wave;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== onDropClick || $[32] !== wave) {
    t12 = <WaveWinners wave={wave} onDropClick={onDropClick} />;
    $[31] = onDropClick;
    $[32] = wave;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== wave) {
    t13 = <MyStreamWaveOutcome wave={wave} />;
    $[34] = wave;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== onDropClick || $[37] !== wave) {
    t14 = <MyStreamWaveMyVotes wave={wave} onDropClick={onDropClick} />;
    $[36] = onDropClick;
    $[37] = wave;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== wave) {
    t15 = <MyStreamWaveFAQ wave={wave} />;
    $[39] = wave;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t10 || $[42] !== t11 || $[43] !== t12 || $[44] !== t13 || $[45] !== t14 || $[46] !== t15) {
    t16 = {
      [MyStreamWaveTab.CHAT]: t10,
      [MyStreamWaveTab.LEADERBOARD]: t11,
      [MyStreamWaveTab.WINNERS]: t12,
      [MyStreamWaveTab.OUTCOME]: t13,
      [MyStreamWaveTab.MY_VOTES]: t14,
      [MyStreamWaveTab.FAQ]: t15
    };
    $[41] = t10;
    $[42] = t11;
    $[43] = t12;
    $[44] = t13;
    $[45] = t14;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  const components = t16;
  let t17;
  if ($[48] !== showGalleryToggle || $[49] !== toggleViewMode || $[50] !== viewMode || $[51] !== wave) {
    t17 = <MyStreamWaveTabs wave={wave} viewMode={viewMode} onToggleViewMode={toggleViewMode} showGalleryToggle={showGalleryToggle} />;
    $[48] = showGalleryToggle;
    $[49] = toggleViewMode;
    $[50] = viewMode;
    $[51] = wave;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== activeContentTab) {
    t18 = getContentTabPanelId(activeContentTab);
    $[53] = activeContentTab;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  const t19 = components[activeContentTab];
  let t20;
  if ($[55] !== t18 || $[56] !== t19) {
    t20 = <div className="tw-relative tw-min-w-0 tw-flex-grow tw-overflow-hidden" role="tabpanel" id={t18}>{t19}</div>;
    $[55] = t18;
    $[56] = t19;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== stableWaveKey || $[59] !== t17 || $[60] !== t20) {
    t21 = <div className="tailwind-scope tw-relative tw-flex tw-h-full tw-min-w-0 tw-flex-col" key={stableWaveKey}>{t17}{t20}</div>;
    $[58] = stableWaveKey;
    $[59] = t17;
    $[60] = t20;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  return t21;
};
export default React.memo(MyStreamWave);